import React, { useState } from 'react';
import { WalletAuth } from '../components/wallet/WalletAuth';
import { useWallet } from '../hooks/useWallet';

export const WalletDemo: React.FC = () => {
  const wallet = useWallet();
  const [messages, setMessages] = useState<string[]>([]);

  const addMessage = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setMessages(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 10));
  };

  const handleAuthSuccess = () => {
    addMessage('✅ Wallet authenticated successfully!');
  };

  const handleAuthError = (error: string) => {
    addMessage(`❌ Authentication error: ${error}`);
  };

  const testSignMessage = async () => {
    try {
      const testMessage = `Test message signed at ${new Date().toISOString()}`;
      const signature = await wallet.signMessage(testMessage);
      addMessage(`✅ Message signed: ${signature.slice(0, 20)}...`);
    } catch (error: any) {
      addMessage(`❌ Signing error: ${error.message}`);
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '40px' }}>
        <h1>Wallet Authentication Demo</h1>
        <p>This page demonstrates the wallet authentication system with session management.</p>
      </div>

      <div style={{ display: 'grid', gap: '30px', gridTemplateColumns: '1fr 1fr' }}>
        {/* Wallet Auth Component */}
        <div>
          <h2>Wallet Authentication</h2>
          <WalletAuth
            onAuthSuccess={handleAuthSuccess}
            onAuthError={handleAuthError}
            showSessionInfo={true}
          />
        </div>

        {/* Status and Testing */}
        <div>
          <h2>Wallet Status</h2>
          <div style={{ 
            background: '#f8fafc', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px', 
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Connection Status:</strong> 
              <span style={{ 
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                background: wallet.isConnected ? '#dcfce7' : '#fef2f2',
                color: wallet.isConnected ? '#166534' : '#dc2626'
              }}>
                {wallet.status.toUpperCase()}
              </span>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <strong>Authentication Status:</strong>
              <span style={{ 
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                background: wallet.isAuthenticated ? '#dcfce7' : '#fef2f2',
                color: wallet.isAuthenticated ? '#166534' : '#dc2626'
              }}>
                {wallet.authStatus.toUpperCase()}
              </span>
            </div>

            {wallet.address && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Address:</strong>
                <div style={{ 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  background: '#f3f4f6',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  marginTop: '4px',
                  wordBreak: 'break-all'
                }}>
                  {wallet.address}
                </div>
              </div>
            )}

            {wallet.sessionToken && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Session Token:</strong>
                <div style={{ 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  background: '#f3f4f6',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  marginTop: '4px',
                  wordBreak: 'break-all'
                }}>
                  {wallet.sessionToken}
                </div>
              </div>
            )}

            {wallet.sessionExpiresAt && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Session Expires:</strong>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  {new Date(wallet.sessionExpiresAt).toLocaleString()}
                  {wallet.sessionExpiresInDays >= 1 && (
                    <span style={{ color: '#10b981', marginLeft: '8px' }}>
                      ({wallet.sessionExpiresInDays} days remaining)
                    </span>
                  )}
                  {wallet.sessionExpired && (
                    <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                      (EXPIRED)
                    </span>
                  )}
                </div>
              </div>
            )}

            {(wallet.error || wallet.authError) && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: '#dc2626' }}>Error:</strong>
                <div style={{ 
                  color: '#dc2626',
                  fontSize: '14px',
                  marginTop: '4px',
                  background: '#fef2f2',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #fecaca'
                }}>
                  {wallet.error || wallet.authError}
                </div>
              </div>
            )}
          </div>

          {/* Test Actions */}
          <h3>Test Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={testSignMessage}
              disabled={!wallet.isConnected}
              style={{
                padding: '8px 16px',
                background: wallet.isConnected ? '#3b82f6' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: wallet.isConnected ? 'pointer' : 'not-allowed'
              }}
            >
              Test Sign Message
            </button>

            <button
              onClick={wallet.checkAuthStatus}
              disabled={!wallet.isConnected || wallet.authStatus === 'checking'}
              style={{
                padding: '8px 16px',
                background: (!wallet.isConnected || wallet.authStatus === 'checking') ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: (!wallet.isConnected || wallet.authStatus === 'checking') ? 'not-allowed' : 'pointer'
              }}
            >
              {wallet.authStatus === 'checking' ? 'Checking...' : 'Refresh Auth Status'}
            </button>
          </div>
        </div>
      </div>

      {/* Message Log */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3>Event Log</h3>
          <button
            onClick={clearMessages}
            style={{
              padding: '4px 12px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
        </div>
        
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px',
          minHeight: '200px',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          {messages.length === 0 ? (
            <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>
              No events yet. Try connecting and authenticating your wallet.
            </div>
          ) : (
            <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
              {messages.map((message, index) => (
                <div key={index} style={{ marginBottom: '8px', lineHeight: '1.4' }}>
                  {message}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '40px', padding: '20px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #0ea5e9' }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#0c4a6e' }}>How to Test</h3>
        <ol style={{ margin: 0, paddingLeft: '20px', color: '#075985' }}>
          <li>Make sure MetaMask is installed and unlocked</li>
          <li>Click "Connect MetaMask" or "Connect & Authenticate"</li>
          <li>Approve the connection in MetaMask</li>
          <li>If not authenticated, click "Authenticate Wallet"</li>
          <li>Sign the authentication message in MetaMask</li>
          <li>Your 30-day session will be created and stored</li>
          <li>Use "Test Sign Message" to verify wallet functionality</li>
          <li>Session will auto-renew when you use the wallet within 7 days of expiry</li>
        </ol>
      </div>
    </div>
  );
};

export default WalletDemo; 